import React, { useState, useEffect } from 'react';
import { useLazyQuery, useLazyCommand, useEvent } from '@soap/modules';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Card } from '../../ui/card';
import { Input } from '../../ui/input';
import { X, Plus } from 'lucide-react';

interface Tag {
  id: string;
  title: string;
}

interface FormData {
  C186_AssessmentId: string;
}

function CreateTagDialog({ 
  onClose, 
  onCreate 
}: { 
  onClose: () => void; 
  onCreate: (tagName: string, tagDescription: string) => void;
}) {
  const [tagName, setTagName] = useState('');
  const [tagDescription, setTagDescription] = useState('');
  const [creating, setCreating] = useState(false);

  const handleCreate = async () => {
    if (!tagName.trim()) return;
    
    setCreating(true);
    onCreate(tagName.trim(), tagDescription.trim());
    setCreating(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <Card className="w-full max-w-md p-6 m-4">
        <h3 className="text-lg font-semibold mb-4">Create New Tag</h3>
        <div className="space-y-4">
          <div>
            <Label htmlFor="tag-name">Tag Name *</Label>
            <Input
              id="tag-name"
              value={tagName}
              onChange={(e) => setTagName(e.target.value)}
              placeholder="Enter tag name"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="tag-description">Description (optional)</Label>
            <Input
              id="tag-description"
              value={tagDescription}
              onChange={(e) => setTagDescription(e.target.value)}
              placeholder="Enter tag description"
              className="mt-1"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} disabled={creating}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreate} 
              disabled={!tagName.trim() || creating}
            >
              {creating ? 'Creating...' : 'Create Tag'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

export function EditAssessmentTags({ assessmentId }: { assessmentId: string }) {
  const [getFormData] = useLazyQuery("C187v1_GetC186FormData");
  const [submitCommand] = useLazyCommand("C186v1_EditAssessmentTags");
  const [createTagCommand] = useLazyCommand("C120v1_AddTag");
  const [formData, setFormData] = useState<FormData | null>(null);
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showCreateTag, setShowCreateTag] = useState(false);

  useEffect(() => {
    if (assessmentId) {
      getFormData({
        C187_AssessmentId: assessmentId
      });
    }
  }, [assessmentId]);

  useEvent({
    eventName: "E153v1_GotC186FormData",
    onEventReceived: (event: any) => {
      // The event contains e000_FieldData with flattened field structure (camelCase in browser)
      const fieldData = event.e000_FieldData || [];
      
      // Find the C186_UpdatedTags field
      const tagsField = fieldData.find((field: any) => field.name === 'c186_UpdatedTags');
      const assessmentIdField = fieldData.find((field: any) => field.name === 'c186_AssessmentId');
      
      if (tagsField && tagsField.initialValue) {
        const tagData = tagsField.initialValue;
        
        // The initialValue structure from backend has PascalCase keys
        // We need to check both PascalCase (from backend) and camelCase (after browser processing)
        const allEnumerations = tagData.AllEnumerations || tagData.allEnumerations || [];
        const selectedKeys = tagData.SelectedKeys || tagData.selectedKeys || [];
        
        const allTags: Tag[] = allEnumerations.map((t: any) => ({
          id: t.Key || t.key,
          title: t.Value || t.value
        }));
        
        const selected = allTags.filter(t => selectedKeys.includes(t.id));
        
        setFormData({
          C186_AssessmentId: assessmentIdField?.initialValue || assessmentId
        });
        setAvailableTags(allTags);
        setSelectedTags(selected);
        setLoading(false);
      } else {
        setLoading(false);
      }
    }
  }, [assessmentId]);

  useEvent({
    eventName: "E149v1_AssessmentUpdated",
    onEventReceived: () => {
      setSaving(false);
    }
  }, []);

  useEvent({
    eventName: "E105v1_TagAdded",
    onEventReceived: (event: any) => {
      // Only refresh if the added tag is for assessments
      if (event.e105_TagTypeKey === "assessment") {
        // Refresh the form data to get the new tag
        if (assessmentId) {
          getFormData({
            C187_AssessmentId: assessmentId
          });
        }
      }
    }
  }, [assessmentId]);

  const handleTagToggle = (tag: Tag) => {
    const isSelected = selectedTags.some(t => t.id === tag.id);
    
    if (isSelected) {
      setSelectedTags(selectedTags.filter(t => t.id !== tag.id));
    } else {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  const handleCreateTag = async (tagName: string, tagDescription: string) => {
    createTagCommand({
      $type: "C120v1_AddTag",
      C120_TagName: tagName,
      C120_TagDescription: tagDescription,
      C120_TagTypeKey: "assessment"
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData) {
      return;
    }

    setSaving(true);
    submitCommand({
      $type: "C186v1_EditAssessmentTags",
      C186_AssessmentId: formData.C186_AssessmentId,
      C186_UpdatedTags: {
        SelectedKeys: selectedTags.map(t => t.id)
      }
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-action-blue"></div>
      </div>
    );
  }

  if (!formData) return null;

  return (
    <Card className="p-6">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <Label className="text-sm font-medium mb-3 block">
            Assessment Tags
          </Label>
          <p className="text-sm text-gray-600 mb-4">
            Select tags to categorize and organize this assessment.
          </p>
          
          {/* Selected Tags */}
          {selectedTags.length > 0 && (
            <div className="mb-4">
              <p className="text-sm font-medium text-gray-700 mb-2">Selected Tags:</p>
              <div className="flex flex-wrap gap-2">
                {selectedTags.map(tag => (
                  <span
                    key={tag.id}
                    className="inline-flex items-center gap-1 bg-action-blue/15 text-action-blue-dark px-3 py-1 rounded-full text-sm"
                  >
                    {tag.title}
                    <button
                      type="button"
                      onClick={() => handleTagToggle(tag)}
                      className="hover:bg-action-blue/25 rounded-full p-0.5"
                    >
                      <X size={14} />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Available Tags */}
          {availableTags.length > 0 ? (
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-gray-700">Available Tags:</p>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setShowCreateTag(true)}
                  className="text-xs"
                >
                  <Plus size={14} className="mr-1" />
                  Create New Tag
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {availableTags
                  .filter(tag => !selectedTags.some(t => t.id === tag.id))
                  .map(tag => (
                    <button
                      key={tag.id}
                      type="button"
                      onClick={() => handleTagToggle(tag)}
                      className="inline-flex items-center bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm hover:bg-gray-200 transition-colors"
                    >
                      {tag.title}
                    </button>
                  ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-sm text-gray-500 mb-3">No tags available yet.</p>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateTag(true)}
              >
                <Plus size={16} className="mr-2" />
                Create Your First Tag
              </Button>
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <Button type="submit" disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </form>

      {showCreateTag && (
        <CreateTagDialog
          onClose={() => setShowCreateTag(false)}
          onCreate={handleCreateTag}
        />
      )}
    </Card>
  );
}

